/**
 * VÉRAULT Portal — DEV / Test Data Cleanup
 *
 * Advisor-only utility (no public/client exposure) for safely removing
 * test data accumulated during development.  Workflow:
 *
 *   1. Configure a filter (emails, email substrings, tags, sources,
 *      name substrings, created-after date).
 *   2. Click "Preview" — server returns per-collection counts + 5-row
 *      samples per collection.  Nothing is deleted.
 *   3. Review samples; click "Delete matching records" — confirmation
 *      modal lists exactly what will be wiped before proceeding.
 *
 * The bare endpoint refuses an empty filter; the UI refuses to submit
 * without at least one populated criterion.
 */
import React from "react";
import { motion } from "framer-motion";
import { Trash2, Search, AlertTriangle, CheckCircle2, Loader2, Tag } from "lucide-react";

import {
  Surface,
  PageHeader,
  EditorialRule,
  PortalBreadcrumb,
  PortalButton,
  Heading,
  api,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

const COLLECTIONS = [
  { key: "travel_clients", label: "Clients" },
  { key: "inquiries", label: "Inquiries" },
  { key: "trips", label: "Experiences" },
  { key: "notifications", label: "Notifications" },
  { key: "nylas_webhook_events", label: "Nylas webhook events" },
];

function parseLines(text) {
  return (text || "")
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function TestDataCleanup() {
  // ---- form state ----
  const [emails, setEmails] = React.useState("");
  const [emailContains, setEmailContains] = React.useState("phase, pytest, test_, idem");
  const [tags, setTags] = React.useState("test, dev_test");
  const [sources, setSources] = React.useState("nylas_scheduler, nylas_webhook");
  const [nameContains, setNameContains] = React.useState("Phase, Pytest, Test");
  const [createdAfter, setCreatedAfter] = React.useState("");

  // ---- result state ----
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [deleted, setDeleted] = React.useState(null);
  const [tagged, setTagged] = React.useState(null);

  const buildFilter = () => ({
    emails: parseLines(emails),
    email_contains: parseLines(emailContains),
    tags: parseLines(tags),
    sources: parseLines(sources),
    name_contains: parseLines(nameContains),
    created_after: createdAfter ? new Date(createdAfter).toISOString() : null,
  });

  const hasAnyFilter = () => {
    const f = buildFilter();
    return (
      f.emails.length ||
      f.email_contains.length ||
      f.tags.length ||
      f.sources.length ||
      f.name_contains.length ||
      f.created_after
    );
  };

  const runPreview = async () => {
    setErr("");
    setDeleted(null);
    setTagged(null);
    if (!hasAnyFilter()) {
      setErr("Add at least one filter criterion before previewing.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/dev/test-data/preview", buildFilter());
      setPreview(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Preview failed.");
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async () => {
    setShowConfirm(false);
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(
        "/dev/test-data/delete?confirm=true",
        buildFilter()
      );
      setDeleted(data.deleted);
      setPreview(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  const runTag = async () => {
    if (!hasAnyFilter()) {
      setErr("Add at least one filter criterion before tagging.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/dev/test-data/tag?tag=test`, buildFilter());
      setTagged(data.tagged);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Tag failed.");
    } finally {
      setBusy(false);
    }
  };

  const totalMatches = preview
    ? Object.values(preview.counts || {}).reduce((s, n) => s + n, 0)
    : 0;

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10">
      <motion.div variants={fadeInUp}>
        <PortalBreadcrumb
          parent={{ to: "/portal/advisor", label: "Studio" }}
          trail={[{ label: "Settings", to: "/portal/advisor/settings" }, { label: "Test Data Cleanup" }]}
        />
        <PageHeader
          eyebrow="Developer · Maintenance"
          title="Test Data Cleanup"
          lede="Safely sweep development artifacts — clients, inquiries, experiences, notifications — without touching production data. Preview first, then confirm."
        />
      </motion.div>

      <motion.div variants={fadeInUp}>
        <Surface variant="paper" padded data-testid="cleanup-filter-card">
          <Heading level="eyebrow">Filter (match ANY criterion)</Heading>
          <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))] mb-8">
            Records matching <em>any</em> of these fields will be selected. Leave a field blank to ignore it.
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field
              label="Exact emails"
              hint="One per line or comma-separated"
              value={emails}
              onChange={setEmails}
              multiline
              testid="cleanup-emails"
            />
            <Field
              label="Email contains"
              hint="Case-insensitive substring (e.g. `phase`, `pytest`)"
              value={emailContains}
              onChange={setEmailContains}
              multiline
              testid="cleanup-email-contains"
            />
            <Field
              label="Tags include"
              hint="Records whose `tags` array contains any listed value"
              value={tags}
              onChange={setTags}
              testid="cleanup-tags"
            />
            <Field
              label="Source equals"
              hint="e.g. `nylas_scheduler`, `nylas_webhook`"
              value={sources}
              onChange={setSources}
              testid="cleanup-sources"
            />
            <Field
              label="Name contains"
              hint="Matches client full_name or trip name"
              value={nameContains}
              onChange={setNameContains}
              testid="cleanup-name-contains"
            />
            <Field
              label="Created on/after"
              hint="ISO date — sweeps everything from this date forward"
              value={createdAfter}
              onChange={setCreatedAfter}
              type="date"
              testid="cleanup-created-after"
            />
          </div>

          <div className="mt-10 pt-6 border-t border-[rgba(0,0,0,0.05)] flex flex-wrap items-center justify-end gap-3">
            {err && (
              <span className="mr-auto font-accent italic text-[13px] text-[hsl(var(--accent-terracotta))]" data-testid="cleanup-error">
                {err}
              </span>
            )}
            <PortalButton
              variant="ghost"
              size="sm"
              onClick={runTag}
              disabled={busy}
              icon={busy ? <Loader2 className="size-3.5 animate-spin" /> : <Tag className="size-3.5" strokeWidth={1.5} />}
              data-testid="cleanup-tag-btn"
            >
              Tag matches as &quot;test&quot;
            </PortalButton>
            <PortalButton
              variant="primary"
              size="sm"
              onClick={runPreview}
              disabled={busy}
              icon={busy ? <Loader2 className="size-3.5 animate-spin" /> : <Search className="size-3.5" strokeWidth={1.5} />}
              data-testid="cleanup-preview-btn"
            >
              Preview
            </PortalButton>
          </div>
        </Surface>
      </motion.div>

      {tagged && (
        <motion.div variants={fadeInUp}>
          <Surface variant="paper" padded data-testid="cleanup-tagged-result">
            <Heading level="eyebrow">Tagged</Heading>
            <p className="mt-2 font-body text-[14px]">
              Added the &quot;test&quot; tag to{" "}
              <span className="font-mono">{tagged.travel_clients || 0}</span> client(s) and{" "}
              <span className="font-mono">{tagged.trips || 0}</span> trip(s).
            </p>
          </Surface>
        </motion.div>
      )}

      {preview && (
        <motion.div variants={fadeInUp}>
          <Surface variant="paper" padded data-testid="cleanup-preview-result">
            <Heading level="eyebrow">Preview · {totalMatches} record{totalMatches === 1 ? "" : "s"}</Heading>
            <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))] mb-6">
              Nothing has been deleted yet. Review the samples below, then confirm.
            </p>
            <div className="space-y-5">
              {COLLECTIONS.map(({ key, label }) => {
                const n = preview.counts?.[key] || 0;
                if (n === 0) return null;
                const rows = preview.samples?.[key] || [];
                return (
                  <div key={key} data-testid={`cleanup-collection-${key}`}>
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                        {label}
                      </span>
                      <span className="font-mono text-[11px] text-[hsl(var(--accent-terracotta))]">
                        {n} match{n === 1 ? "" : "es"}
                      </span>
                    </div>
                    <ul className="space-y-1 pl-4 border-l border-[rgba(0,0,0,0.06)]" data-testid={`cleanup-sample-${key}`}>
                      {rows.map((row, i) => (
                        <li key={i} className="font-mono text-[11.5px] text-[hsl(var(--foreground-secondary))] truncate">
                          {row.id?.slice(0, 8) || row.received_at?.slice(0, 10) || "—"} · {row.full_name || row.name || row.internal_trip_name || row.notification_type || ""} {row.email ? ` · ${row.email}` : ""}
                        </li>
                      ))}
                      {n > rows.length && (
                        <li className="font-mono text-[11px] text-[hsl(var(--foreground-muted))] italic">
                          …and {n - rows.length} more
                        </li>
                      )}
                    </ul>
                  </div>
                );
              })}
            </div>
            <div className="mt-8 pt-6 border-t border-[rgba(0,0,0,0.05)] flex justify-end">
              <PortalButton
                variant="danger"
                size="sm"
                onClick={() => setShowConfirm(true)}
                disabled={busy || totalMatches === 0}
                icon={<Trash2 className="size-3.5" strokeWidth={1.5} />}
                data-testid="cleanup-delete-btn"
              >
                Delete {totalMatches} record{totalMatches === 1 ? "" : "s"}
              </PortalButton>
            </div>
          </Surface>
        </motion.div>
      )}

      {deleted && (
        <motion.div variants={fadeInUp}>
          <Surface variant="paper" padded data-testid="cleanup-deleted-result">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="size-5 text-[hsl(var(--accent-sage))] shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <Heading level="eyebrow">Wiped</Heading>
                <ul className="mt-3 space-y-1 font-mono text-[12px] text-[hsl(var(--foreground-secondary))]">
                  {COLLECTIONS.map(({ key, label }) => (
                    <li key={key}>
                      {label}: <span className="text-[hsl(var(--foreground))]">{deleted[key] || 0}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Surface>
        </motion.div>
      )}

      {/* Confirmation modal */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-50 bg-[hsl(var(--foreground))]/40 backdrop-blur-sm flex items-center justify-center p-6"
          data-testid="cleanup-confirm-modal"
        >
          <Surface variant="paper" padded className="max-w-md w-full">
            <div className="flex items-start gap-3 mb-5">
              <AlertTriangle className="size-5 text-[hsl(var(--accent-terracotta))] shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <Heading level="eyebrow">Confirm permanent delete</Heading>
                <p className="mt-2 font-body text-[14px] text-[hsl(var(--foreground-secondary))]">
                  This will permanently remove <span className="font-mono text-[hsl(var(--foreground))]">{totalMatches}</span>{" "}
                  record{totalMatches === 1 ? "" : "s"} across {COLLECTIONS.filter(({ key }) => (preview?.counts?.[key] || 0) > 0).length} collection{COLLECTIONS.filter(({ key }) => (preview?.counts?.[key] || 0) > 0).length === 1 ? "" : "s"}. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-5 border-t border-[rgba(0,0,0,0.05)]">
              <PortalButton variant="ghost" size="sm" onClick={() => setShowConfirm(false)} data-testid="cleanup-confirm-cancel">
                Cancel
              </PortalButton>
              <PortalButton variant="danger" size="sm" onClick={runDelete} data-testid="cleanup-confirm-delete">
                Yes, delete
              </PortalButton>
            </div>
          </Surface>
        </div>
      )}
    </motion.div>
  );
}

function Field({ label, hint, value, onChange, multiline, type = "text", testid }) {
  const cls =
    "w-full px-3 py-2 text-[13px] font-body bg-[hsl(var(--background))] border border-transparent rounded-md outline-none focus:border-[rgba(0,0,0,0.12)] transition-colors";
  return (
    <div>
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1.5 block">
        {label}
      </span>
      {multiline ? (
        <textarea
          rows={3}
          className={cls}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
        />
      ) : (
        <input
          type={type}
          className={cls}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
        />
      )}
      {hint && <p className="mt-1.5 font-accent italic text-[11.5px] text-[hsl(var(--foreground-muted))]">{hint}</p>}
    </div>
  );
}
